// Faithful port of lodash's debounce/throttle timing semantics (leading/trailing/maxWait edges,
// plus .cancel() and .flush()). throttle is defined in terms of debounce with maxWait === wait,
// exactly as lodash does. Backed by setTimeout + Date.now().

export type DebounceSettings = {
  leading?: boolean;
  maxWait?: number;
  trailing?: boolean;
};

export type ThrottleSettings = {
  leading?: boolean;
  trailing?: boolean;
};

export type DebouncedFunc<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel: () => void;
  flush: () => ReturnType<T> | undefined;
};

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait = 0,
  options: DebounceSettings = {},
): DebouncedFunc<T> {
  let lastArgs: Parameters<T> | undefined;
  let lastThis: unknown;
  let result: ReturnType<T> | undefined;
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;

  const waitMS = Number(wait) || 0;
  const leading = !!options.leading;
  const trailing = 'trailing' in options ? !!options.trailing : true;
  const maxing = 'maxWait' in options;
  const maxWait = maxing ? Math.max(Number(options.maxWait) || 0, waitMS) : 0;

  function invokeFunc(time: number): ReturnType<T> | undefined {
    const args = lastArgs;
    const thisArg = lastThis;
    lastArgs = undefined;
    lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args as Parameters<T>);
    return result;
  }

  function remainingWait(time: number): number {
    const timeSinceLastCall = time - (lastCallTime as number);
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = waitMS - timeSinceLastCall;
    return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = time - (lastCallTime as number);
    const timeSinceLastInvoke = time - lastInvokeTime;
    return (
      lastCallTime === undefined
      || timeSinceLastCall >= waitMS
      || timeSinceLastCall < 0
      || (maxing && timeSinceLastInvoke >= maxWait)
    );
  }

  function trailingEdge(time: number): ReturnType<T> | undefined {
    timerId = undefined;
    if (trailing && lastArgs) return invokeFunc(time);
    lastArgs = undefined;
    lastThis = undefined;
    return result;
  }

  function timerExpired(): void {
    const time = Date.now();
    if (shouldInvoke(time)) {
      trailingEdge(time);
      return;
    }
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function leadingEdge(time: number): ReturnType<T> | undefined {
    lastInvokeTime = time;
    timerId = setTimeout(timerExpired, waitMS);
    return leading ? invokeFunc(time) : result;
  }

  function cancel(): void {
    if (timerId !== undefined) clearTimeout(timerId);
    lastInvokeTime = 0;
    lastArgs = undefined;
    lastCallTime = undefined;
    lastThis = undefined;
    timerId = undefined;
  }

  function flush(): ReturnType<T> | undefined {
    return timerId === undefined ? result : trailingEdge(Date.now());
  }

  function debounced(this: unknown, ...args: Parameters<T>): ReturnType<T> | undefined {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);
    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) return leadingEdge(lastCallTime);
      if (maxing) {
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, waitMS);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, waitMS);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced as DebouncedFunc<T>;
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait = 0,
  options: ThrottleSettings = {},
): DebouncedFunc<T> {
  const leading = 'leading' in options ? !!options.leading : true;
  const trailing = 'trailing' in options ? !!options.trailing : true;
  return debounce(func, wait, { leading, maxWait: wait, trailing });
}
